import React, { useState } from 'react';
import { X, Download, Check } from 'lucide-react';
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink, pdf } from '@react-pdf/renderer';

// PDF Styles
const pdfStyles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Helvetica',
    fontSize: 10,
    lineHeight: 1.6,
  },
  header: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
    color: '#0095fa',
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: 15,
    marginBottom: 10,
    color: '#0f6caa',
    borderBottom: '2px solid #0095fa',
    paddingBottom: 5,
  },
  fieldRow: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 10,
    paddingBottom: 5,
    borderBottom: '1px solid #e0e0e0',
  },
  fieldLabel: {
    fontWeight: 'bold',
    width: '40%',
    color: '#333',
  },
  fieldValue: {
    width: '60%',
    color: '#666',
    wordBreak: 'break-word',
  },
  signature: {
    marginTop: 30,
    borderTop: '1px solid #333',
    paddingTop: 10,
  },
});

// PDF Document Component
const HRFormPDF = ({ formData }) => (
  <Document>
    <Page size="A4" style={pdfStyles.page}>
      {/* Header */}
      <Text style={pdfStyles.header}>HR Form Review</Text>

      {/* Cover Sheet Section */}
      <Text style={pdfStyles.sectionTitle}>Cover Sheet</Text>
      <View>
        {formData.employeeName && (
          <View style={pdfStyles.fieldRow}>
            <Text style={pdfStyles.fieldLabel}>Employee Name:</Text>
            <Text style={pdfStyles.fieldValue}>{formData.employeeName}</Text>
          </View>
        )}
        {formData.dob && (
          <View style={pdfStyles.fieldRow}>
            <Text style={pdfStyles.fieldLabel}>Date of Birth:</Text>
            <Text style={pdfStyles.fieldValue}>{formData.dob}</Text>
          </View>
        )}
        {formData.email && (
          <View style={pdfStyles.fieldRow}>
            <Text style={pdfStyles.fieldLabel}>Email:</Text>
            <Text style={pdfStyles.fieldValue}>{formData.email}</Text>
          </View>
        )}
        {formData.employeePhone && (
          <View style={pdfStyles.fieldRow}>
            <Text style={pdfStyles.fieldLabel}>Phone:</Text>
            <Text style={pdfStyles.fieldValue}>{formData.employeePhone}</Text>
          </View>
        )}
        {formData.clientName && (
          <View style={pdfStyles.fieldRow}>
            <Text style={pdfStyles.fieldLabel}>Client Name:</Text>
            <Text style={pdfStyles.fieldValue}>{formData.clientName}</Text>
          </View>
        )}
      </View>

      {/* Application for Employment */}
      <Text style={pdfStyles.sectionTitle}>Application for Employment</Text>
      <View>
        {formData.firstName && (
          <View style={pdfStyles.fieldRow}>
            <Text style={pdfStyles.fieldLabel}>First Name:</Text>
            <Text style={pdfStyles.fieldValue}>{formData.firstName}</Text>
          </View>
        )}
        {formData.lastName && (
          <View style={pdfStyles.fieldRow}>
            <Text style={pdfStyles.fieldLabel}>Last Name:</Text>
            <Text style={pdfStyles.fieldValue}>{formData.lastName}</Text>
          </View>
        )}
        {formData.positionDesired && (
          <View style={pdfStyles.fieldRow}>
            <Text style={pdfStyles.fieldLabel}>Position Desired:</Text>
            <Text style={pdfStyles.fieldValue}>{formData.positionDesired}</Text>
          </View>
        )}
        {formData.employmentType && (
          <View style={pdfStyles.fieldRow}>
            <Text style={pdfStyles.fieldLabel}>Employment Type:</Text>
            <Text style={pdfStyles.fieldValue}>{formData.employmentType}</Text>
          </View>
        )}
      </View>

      {/* W-4 Form */}
      <Text style={pdfStyles.sectionTitle}>W-4 Form</Text>
      <View>
        {formData.w4FirstName && (
          <View style={pdfStyles.fieldRow}>
            <Text style={pdfStyles.fieldLabel}>Name:</Text>
            <Text style={pdfStyles.fieldValue}>
              {formData.w4FirstName} {formData.w4LastName}
            </Text>
          </View>
        )}
        {formData.w4FilingStatus && (
          <View style={pdfStyles.fieldRow}>
            <Text style={pdfStyles.fieldLabel}>Filing Status:</Text>
            <Text style={pdfStyles.fieldValue}>{formData.w4FilingStatus}</Text>
          </View>
        )}
      </View>

      {/* I-9 Form */}
      <Text style={pdfStyles.sectionTitle}>I-9 Employment Eligibility</Text>
      <View>
        {formData.i9FirstName && (
          <View style={pdfStyles.fieldRow}>
            <Text style={pdfStyles.fieldLabel}>Name:</Text>
            <Text style={pdfStyles.fieldValue}>
              {formData.i9FirstName} {formData.i9LastName}
            </Text>
          </View>
        )}
        {formData.i9CitizenStatus && (
          <View style={pdfStyles.fieldRow}>
            <Text style={pdfStyles.fieldLabel}>Citizen Status:</Text>
            <Text style={pdfStyles.fieldValue}>{formData.i9CitizenStatus}</Text>
          </View>
        )}
      </View>

      {/* Signatures */}
      <Text style={pdfStyles.sectionTitle}>Signatures & Acknowledgments</Text>
      <View style={pdfStyles.signature}>
        {formData.applicantSignature && (
          <Text>Applicant Signature: {formData.applicantSignature}</Text>
        )}
        {formData.acknowledgesDuties && (
          <Text>Acknowledges Job Duties: Yes</Text>
        )}
      </View>
    </Page>
  </Document>
);

const PDFPreviewModal = ({ formData, onClose, onSubmit, isEditable }) => {
  const [isLoading, setIsLoading] = useState(false);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-blue-500 bg-gradient-to-r from-blue-50 to-blue-100">
          <h2 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Montserrat' }}>
            PDF Preview
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-blue-200 rounded-lg transition-colors duration-300"
          >
            <X size={24} className="text-gray-600" />
          </button>
        </div>

        {/* PDF Preview Content */}
        <div className="flex-1 overflow-auto bg-gray-50 p-6">
          <div className="bg-white rounded-lg shadow-md p-8 mx-auto" style={{ maxWidth: '8.5in' }}>
            {/* Document Title */}
            <h1 className="text-4xl font-bold text-center mb-8" style={{ color: '#0095fa', fontFamily: 'Montserrat' }}>
              HR Form Review
            </h1>

            {/* Cover Sheet Preview */}
            {(formData.employeeName || formData.clientName) && (
              <div className="mb-8">
                <h3 className="text-lg font-bold mb-4 pb-2 border-b-2" style={{ color: '#0f6caa', borderColor: '#0095fa' }}>
                  Cover Sheet
                </h3>
                <div className="space-y-2 text-sm">
                  {formData.employeeName && <p><span className="font-semibold">Employee Name:</span> {formData.employeeName}</p>}
                  {formData.dob && <p><span className="font-semibold">Date of Birth:</span> {formData.dob}</p>}
                  {formData.email && <p><span className="font-semibold">Email:</span> {formData.email}</p>}
                  {formData.employeePhone && <p><span className="font-semibold">Phone:</span> {formData.employeePhone}</p>}
                  {formData.clientName && <p><span className="font-semibold">Client Name:</span> {formData.clientName}</p>}
                </div>
              </div>
            )}

            {/* Application for Employment Preview */}
            {(formData.firstName || formData.positionDesired) && (
              <div className="mb-8">
                <h3 className="text-lg font-bold mb-4 pb-2 border-b-2" style={{ color: '#0f6caa', borderColor: '#0095fa' }}>
                  Application for Employment
                </h3>
                <div className="space-y-2 text-sm">
                  {formData.firstName && <p><span className="font-semibold">Name:</span> {formData.firstName} {formData.lastName}</p>}
                  {formData.positionDesired && <p><span className="font-semibold">Position Desired:</span> {formData.positionDesired}</p>}
                  {formData.employmentType && <p><span className="font-semibold">Employment Type:</span> {formData.employmentType}</p>}
                </div>
              </div>
            )}

            {/* W-4 Form Preview */}
            {formData.w4FirstName && (
              <div className="mb-8">
                <h3 className="text-lg font-bold mb-4 pb-2 border-b-2" style={{ color: '#0f6caa', borderColor: '#0095fa' }}>
                  W-4 Form
                </h3>
                <div className="space-y-2 text-sm">
                  <p><span className="font-semibold">Name:</span> {formData.w4FirstName} {formData.w4LastName}</p>
                  {formData.w4FilingStatus && <p><span className="font-semibold">Filing Status:</span> {formData.w4FilingStatus}</p>}
                </div>
              </div>
            )}

            {/* I-9 Form Preview */}
            {formData.i9FirstName && (
              <div className="mb-8">
                <h3 className="text-lg font-bold mb-4 pb-2 border-b-2" style={{ color: '#0f6caa', borderColor: '#0095fa' }}>
                  I-9 Employment Eligibility
                </h3>
                <div className="space-y-2 text-sm">
                  <p><span className="font-semibold">Name:</span> {formData.i9FirstName} {formData.i9LastName}</p>
                  {formData.i9CitizenStatus && <p><span className="font-semibold">Citizen Status:</span> {formData.i9CitizenStatus}</p>}
                </div>
              </div>
            )}

            {/* Empty State */}
            {!formData.employeeName && !formData.firstName && !formData.w4FirstName && !formData.i9FirstName && (
              <div className="text-center py-8 text-gray-500">
                <p className="mb-2">No form data to preview</p>
                <p className="text-sm">Fill in the form fields to see the PDF preview</p>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="border-t px-6 py-4 bg-gray-50 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 transition-all duration-300"
          >
            Back to Edit
          </button>
        </div>
      </div>
    </div>
  );
};

export default PDFPreviewModal;